/**
 * Upcoming tab (#991) — every probe due in the next ~3 months, across ALL
 * accounts (no contract filter), badged by who calibrates and split by probe
 * type × where it is calibrated, with a 13-week lab-bench queue.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { MetricCard } from '@/components/MetricCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import BarChart from '@/components/charts/BarChart';
import { CHART_COLORS, type ChartDataPoint } from '@/components/charts/types';
import { DataTable } from '@/shared/components/data-table';
import { downloadCSV, toCSV, type CSVColumn } from '@/lib/csv';
import { cn } from '@/lib/utils';
import { useUpcomingLinesTable } from '../../hooks/useUpcomingLinesTable';
import { PROBE_TYPES, probeTypeLabel, providerLabel, serviceModeLabel, actionLabel } from '../../labels';
import type { CalibrationProvider, ProbeType, UpcomingLine, UpcomingView } from '../../types';

// The matrix "location" axis (richer than the API's serviceMode — splits shipped
// probe-to-lab from étalon-to-client, computed here from the lines).
type Location = 'onsite' | 'tolab' | 'etalon' | 'review';
const LOCATIONS: Location[] = ['onsite', 'tolab', 'etalon', 'review'];
const MATRIX_ROWS: (ProbeType | 'REVIEW')[] = [...PROBE_TYPES, 'REVIEW'];

const PROBE_TYPE_COLOR: Record<ProbeType, string> = {
  ETAL: CHART_COLORS.purple,
  OVEN: CHART_COLORS.primary,
  CRYO: CHART_COLORS.cyan,
  NUM: CHART_COLORS.gray,
  TH: CHART_COLORS.success,
  GAS: CHART_COLORS.danger,
  O2: CHART_COLORS.warning,
};

function locationOf(line: UpcomingLine): Location {
  if (line.serviceMode === 'onsite') return 'onsite';
  if (line.shipKind === 'probe-to-lab') return 'tolab';
  if (line.shipKind === 'etalon-to-client') return 'etalon';
  return 'review';
}

interface UpcomingTabProps {
  upcoming: UpcomingView;
}

export function UpcomingTab({ upcoming }: UpcomingTabProps) {
  const { t } = useTranslation('operations');
  const table = useUpcomingLinesTable(upcoming.lines, t);

  const csvColumns: CSVColumn<UpcomingLine>[] = useMemo(() => [
    { header: t('calibrationPlanning.columns.probeType', 'Probe type'), value: (r) => probeTypeLabel(t, r.probeType) },
    { header: t('calibrationPlanning.columns.serviceMode', 'Service mode'), value: (r) => serviceModeLabel(t, r.serviceMode) },
    { header: t('calibrationPlanning.columns.action', 'Treatment'), value: (r) => actionLabel(t, r.action) },
    { header: t('calibrationPlanning.columns.provider', 'Calibrated by'), value: (r) => providerLabel(t, r.provider) },
    { header: t('calibrationPlanning.columns.account', 'Account'), value: (r) => r.account },
    { header: t('calibrationPlanning.columns.site', 'Site'), value: (r) => r.siteName ?? '' },
    { header: t('calibrationPlanning.columns.sensor', 'Sensor'), value: (r) => r.sensor ?? '' },
    { header: t('calibrationPlanning.columns.channel', 'Channel'), value: (r) => r.channel ?? '' },
    { header: t('calibrationPlanning.columns.model', 'Model'), value: (r) => r.probeModel ?? '' },
    { header: t('calibrationPlanning.columns.region', 'Region'), value: (r) => r.region ?? '' },
    { header: t('calibrationPlanning.columns.room', 'Room'), value: (r) => r.room ?? '' },
    { header: t('calibrationPlanning.columns.dueDate', 'Due date'), value: (r) => r.dueDate },
    { header: t('calibrationPlanning.columns.inDays', 'In N days'), value: (r) => r.daysUntilDue },
  ], [t]);

  // Type × location matrix, computed from the lines (source of truth).
  const matrix = useMemo(() => {
    const m: Record<string, Record<Location, number>> = {};
    for (const row of MATRIX_ROWS) m[row] = { onsite: 0, tolab: 0, etalon: 0, review: 0 };
    for (const line of upcoming.lines) {
      const key = line.probeType ?? 'REVIEW';
      m[key][locationOf(line)] += 1;
    }
    return m;
  }, [upcoming.lines]);

  const setProviderFilter = (provider: CalibrationProvider) => {
    table.clearFilters();
    table.setColumnFilter('provider', JSON.stringify([provider]));
  };

  const onCellClick = (probeType: ProbeType | 'REVIEW', loc: Location) => {
    table.clearFilters();
    if (probeType !== 'REVIEW') table.setColumnFilter('probeType', JSON.stringify([probeType]));
    // The table has no shipKind column — #1118 replaced it with `action`. Inside the
    // shipping lane the two are 1:1 (treatmentAction: mail + lab-calibratable =>
    // ship-replacement-probe, mail + onsite-only => ship-calibrator), so filtering the
    // treatment reproduces the old location filter exactly. A filter naming a column that
    // does not exist is silently ignored by the predicate, which is how this went dead.
    if (loc === 'onsite') table.setColumnFilter('serviceMode', JSON.stringify(['onsite']));
    else if (loc === 'tolab') table.setColumnFilter('action', JSON.stringify(['ship-replacement-probe']));
    else if (loc === 'etalon') table.setColumnFilter('action', JSON.stringify(['ship-calibrator']));
  };

  const weekChartData: ChartDataPoint[] = upcoming.byWeek.map((w) => {
    const point: ChartDataPoint = { name: w.week };
    for (const pt of PROBE_TYPES) point[pt] = w.byProbeType[pt] ?? 0;
    return point;
  });
  const weekChartSeries = PROBE_TYPES.map((pt) => ({ dataKey: pt, name: probeTypeLabel(t, pt), color: PROBE_TYPE_COLOR[pt] }));

  const locationLabel: Record<Location, string> = {
    onsite: t('calibrationPlanning.matrix.onsite', 'On-site'),
    tolab: t('calibrationPlanning.matrix.tolab', 'ATEK lab'),
    etalon: t('calibrationPlanning.matrix.etalon', 'Étalon shipped'),
    review: t('calibrationPlanning.matrix.review', 'To review'),
  };

  const handleExport = () => {
    const filename = `calibration-upcoming-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCSV(filename, toCSV(table.sortedLines, csvColumns));
    toast.success(t('calibrationPlanning.upcoming.exported', 'Exported {{n}} upcoming calibrations', { n: table.sortedLines.length }));
  };

  return (
    <div className="space-y-6">
      {/* Provider tiles — the last three filter the table */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title={t('calibrationPlanning.upcoming.total', 'Due in 3 months')}
          value={upcoming.total}
          color="blue"
          tooltip={t('calibrationPlanning.upcoming.totalTip', 'Every probe due within the next 3 months, across all accounts.')}
        />
        <button type="button" onClick={() => setProviderFilter('atek')} className="block w-full text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <MetricCard title={t('calibrationPlanning.upcoming.byAtek', 'By ATEK')} value={upcoming.byProvider.atek} color="green" />
        </button>
        <button type="button" onClick={() => setProviderFilter('client')} className="block w-full text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <MetricCard title={t('calibrationPlanning.upcoming.byClient', 'By client')} value={upcoming.byProvider.client} color="purple" />
        </button>
        <button type="button" onClick={() => setProviderFilter('other')} className="block w-full text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <MetricCard title={t('calibrationPlanning.upcoming.toVerify', 'To verify')} value={upcoming.byProvider.other} color="orange" />
        </button>
      </div>

      {/* Type × location matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('calibrationPlanning.matrix.title', 'Probe type × where it is calibrated')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[560px] gap-px bg-border text-sm"
              style={{ gridTemplateColumns: `minmax(140px,1.4fr) repeat(${LOCATIONS.length}, minmax(0,1fr))` }}
              role="grid"
              aria-label={t('calibrationPlanning.matrix.title', 'Probe type × where it is calibrated')}
            >
              <div className="bg-muted/50 px-3 py-2 font-medium" role="columnheader">{t('calibrationPlanning.matrix.probeType', 'Probe type')}</div>
              {LOCATIONS.map((loc) => (
                <div key={loc} className="bg-muted/50 px-3 py-2 text-right font-medium" role="columnheader">{locationLabel[loc]}</div>
              ))}
              {MATRIX_ROWS.map((pt) => (
                <div key={pt} className="contents" role="row">
                  <div className="bg-background px-3 py-2 font-medium" role="rowheader">
                    {pt === 'REVIEW' ? t('calibrationPlanning.probeType.unclassified', 'Unclassified') : probeTypeLabel(t, pt)}
                  </div>
                  {LOCATIONS.map((loc) => {
                    const n = matrix[pt][loc];
                    return (
                      <button
                        key={loc}
                        type="button"
                        disabled={n === 0}
                        onClick={() => onCellClick(pt, loc)}
                        className={cn(
                          'bg-background px-3 py-2 text-right tabular-nums transition-colors',
                          n > 0 ? 'hover:bg-accent cursor-pointer' : 'text-muted-foreground/40 cursor-default',
                        )}
                        aria-label={`${pt} — ${locationLabel[loc]}: ${n}`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('calibrationPlanning.matrix.note', 'A split-location sensor (CO2TH / THL) appears in two cells: its gas leg on-site and its temp/humidity leg at the lab. Click a cell to filter the table.')}
          </p>
        </CardContent>
      </Card>

      {/* 13-week lab queue */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('calibrationPlanning.queue.title', '13-week lab queue')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <BarChart data={weekChartData} series={weekChartSeries} xAxisKey="name" stacked showLegend height={260} />
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">{t('calibrationPlanning.queue.week', 'Week')}</TableHead>
                  <TableHead scope="col" className="text-right">{t('calibrationPlanning.queue.total', 'Total')}</TableHead>
                  <TableHead scope="col" className="text-right">{t('calibrationPlanning.queue.toLab', 'ATEK lab')}</TableHead>
                  <TableHead scope="col" className="text-right">{t('calibrationPlanning.queue.etalon', 'Étalon shipped')}</TableHead>
                  <TableHead scope="col" className="text-right">{t('calibrationPlanning.queue.onsite', 'On-site')}</TableHead>
                  <TableHead scope="col" className="text-right">{t('calibrationPlanning.queue.labDays', 'Lab days (ATEK)')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.byWeek.map((w) => (
                  <TableRow key={w.week} className={w.overCapacity ? 'bg-red-50 dark:bg-red-950/30' : undefined}>
                    <TableCell>{w.week}</TableCell>
                    <TableCell className="text-right tabular-nums">{w.total}</TableCell>
                    <TableCell className="text-right tabular-nums">{w.toLab}</TableCell>
                    <TableCell className="text-right tabular-nums">{w.etalonToClient}</TableCell>
                    <TableCell className="text-right tabular-nums">{w.onsite}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {w.overCapacity ? (
                        <Badge variant="danger">{w.labDays}</Badge>
                      ) : (
                        w.labDays
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Per-probe table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">
            {t('calibrationPlanning.upcoming.tableTitle', 'Upcoming probes')} ({upcoming.total})
          </h2>
          <div className="flex items-center gap-1">
            {Object.values(table.columnFilters).some(Boolean) && (
              <Button variant="outline" size="sm" onClick={table.clearFilters}>
                {t('calibrationPlanning.upcoming.clearFilters', 'Clear filters')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-1 h-4 w-4" /> {t('calibrationPlanning.upcoming.export', 'Export')}
            </Button>
          </div>
        </div>
        <DataTable
          items={table.sortedLines}
          columns={table.columns}
          ctx={undefined}
          columnFilters={table.columnFilters}
          sortKey={table.sortKey}
          sortDirection={table.sortDirection}
          onSort={table.handleSort}
          onSetColumnFilter={table.setColumnFilter}
          getRowKey={(row) => `${row.orgId ?? row.account}-${row.channel ?? ''}-${row.certNumber ?? ''}-${row.dueDate}`}
          emptyState={t('calibrationPlanning.upcoming.empty', 'No upcoming calibrations match these filters.')}
        />
      </div>
    </div>
  );
}
